import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

/**
 * Generic hook that handles a Stripe Checkout return URL parameter.
 * @param {string}   paramName   - URL search param to watch (e.g. 'audiobook_production')
 * @param {string}   successMsg  - Toast message on success
 * @param {string}   cancelMsg   - Toast message on cancel
 * @param {Function} onSuccess   - Callback invoked when param === 'success'
 * @param {object}   bannerInfo  - Optional rich banner data: { headline, body, ctaLabel }
 *                                 If provided, fires onBanner(bannerInfo) instead of toast.
 * @param {Function} onBanner    - Called with bannerInfo when purchase succeeds
 */
export function useStripeReturn(paramName, successMsg, cancelMsg, onSuccess, bannerInfo, onBanner) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Use refs for callbacks to avoid re-running the effect when they change.
  // This prevents infinite loops caused by unstable function/object references.
  const onSuccessRef = useRef(onSuccess);
  const onBannerRef = useRef(onBanner);
  const bannerInfoRef = useRef(bannerInfo);

  // Keep refs up-to-date without triggering re-runs of the main effect.
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onBannerRef.current = onBanner;
    bannerInfoRef.current = bannerInfo;
  });

  useEffect(() => {
    const flag = searchParams.get(paramName);
    if (!flag) return;
    if (flag === 'success') {
      if (bannerInfoRef.current && onBannerRef.current) {
        onBannerRef.current(bannerInfoRef.current);
      } else {
        toast.success(successMsg);
      }
      onSuccessRef.current?.();
    } else if (flag === 'cancelled') {
      toast.info(cancelMsg);
    }
    navigate(window.location.pathname, { replace: true });
  }, [searchParams, navigate, paramName, successMsg, cancelMsg]);
}

/** Editorial order checkout return (?editorial_order=xxx&checkout=success) */
export function useEditorialOrderReturn(onSuccess, onBanner) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const onSuccessRef = useRef(onSuccess);
  const onBannerRef  = useRef(onBanner);

  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onBannerRef.current  = onBanner;
  });

  useEffect(() => {
    const orderId  = searchParams.get('editorial_order');
    const checkout = searchParams.get('checkout');
    if (!orderId) return;
    if (checkout === 'success') {
      onBannerRef.current?.({
        headline: 'Editorial order confirmed!',
        body: 'Your payment was received. We\'ll assign an editor and be in touch shortly.',
        ctaLabel: 'View my order',
      });
      onSuccessRef.current?.();
    } else if (checkout === 'cancelled') {
      toast.info('Editorial checkout cancelled — your order quote is saved.');
    }
    // Clear both params
    const next = new URLSearchParams(searchParams);
    next.delete('editorial_order');
    next.delete('checkout');
    navigate(`${window.location.pathname}${next.toString() ? '?' + next.toString() : ''}`, { replace: true });
  }, [searchParams, navigate]);
}
export function useAudiobookProductionReturn(onSuccess, onBanner) {
  useStripeReturn(
    'audiobook_production',
    'Audiobook production purchased — select a voice and generate!',
    'Audiobook production purchase cancelled.',
    onSuccess,
    {
      headline: 'Audiobook production unlocked!',
      body: 'Select a narrator voice and generate your full audiobook.',
      ctaLabel: 'Generate now',
    },
    onBanner,
  );
}

/** Canva cover credit purchase return (?canva_cover_purchase=success) */
export function useCanvaCoverPurchaseReturn(onSuccess, onBanner) {
  useStripeReturn(
    'canva_cover_purchase',
    'Cover credit added — you can now design a book cover.',
    'Cover purchase cancelled.',
    onSuccess,
    {
      headline: 'Cover credit added to your account!',
      body: 'Start designing your book cover in Canva right now.',
      ctaLabel: 'Design cover',
    },
    onBanner,
  );
}
